import React from "react";
import {
  CompanyCustomerEditModalType,
  Customer,
  DateFormats,
  Department,
  Seniority,
  capitalizeFirstLetter,
  formatDate,
  isDirectorOfCompany,
} from "../../../../core";
import { Controller, useForm } from "react-hook-form";
import ErrorMessage from "../../../../components/text/ErrorMessage";
import { DateInput } from "../../../../components/form-group";
import { DropdownList } from "react-widgets";
import PhoneInput from "react-phone-input-2";
import { useDataLoader } from "react-remote-data-hooks";
import api from "../../../../api";
import { toastr } from "react-redux-toastr";
import { ValidatePhoneNumberRegEx } from "../../../../utils/Validatation";

type EditAssistantForm = {
  editModal: CompanyCustomerEditModalType;
  customerDetails?: Customer;
  setEditModal: React.Dispatch<React.SetStateAction<CompanyCustomerEditModalType>>;
  updateUser: (details: Customer) => void;
  onClose: () => void;
};

const EditAssistantDetails = ({
  editModal,
  customerDetails,
  setEditModal,
  updateUser,
  onClose,
}: EditAssistantForm) => {
  const { control, handleSubmit, errors, watch } = useForm<Customer>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const { data: departmentDetails } = useDataLoader<Department[]>(() =>
    api.department.loadAllDepartments()
  );
  const { data: seniorityDetails } = useDataLoader<Seniority[]>(() =>
    api.seniority.loadAllSeniority()
  );

  const onSubmit = async (data: Customer) => {
    try {
      const response = await api.customer.updateCustomer(data, customerDetails?._id);
      updateUser(response);
      onClose();
      toastr.success("Success", "Details saved successfully");
    } catch (error) {
      if (error?.response?.data?.data?.message) {
        toastr.error("Error", error?.response?.data?.data?.message.join());
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
    }
  };

  return !editModal.isEditAssistantDetails || editModal.id !== customerDetails?._id ? (
    <div className="contact-form-row">
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">{"Assistant Name"}</div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">{customerDetails?.assistantName}</div>
          <i
            onClick={() =>
              setEditModal({
                isEditAssistantDetails: true,
                id: customerDetails?._id,
              })
            }
            className="icon-edit icon-box cursor-pointer d-flex align-items-center justify-content-center border ml-auto contact-edit bg-white"
          ></i>
        </div>
      </div>
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">
          {"Assistant Number"}
        </div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">{customerDetails?.assistantNumber}</div>
        </div>
      </div>
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">
          {"Assistant Designation"}
        </div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">
            {customerDetails?.assistantDesignation}
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">{"Seniority"}</div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">
            {((customerDetails?.seniority as any) as Seniority)?.title}
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">{"Department"}</div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">
            {((customerDetails?.department as any) as Department)?.title || ""}
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">
          {"Association Start Date"}
        </div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">
            {!!customerDetails?.associationStartDate &&
              formatDate(customerDetails?.associationStartDate || "", DateFormats.NormalDateFormat)}
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">
          {"Association End Date"}
        </div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">
            {!!customerDetails?.associationEndDate &&
              formatDate(customerDetails?.associationEndDate || "", DateFormats.NormalDateFormat)}
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">
          {"Director With Company"}
        </div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">
            {capitalizeFirstLetter(customerDetails?.isDirectorOfCompany?.toString() || "")}
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap px-4 py-1  align-items-center flex-wrap">
        <div className="col pl-0 pr-2 contact-label  font-xs text-gray-500 text-right ">
          {"Account Manager"}
        </div>
        <div className="col pl-2 pr-0 contact-content d-flex align-items-center">
          <div className="font-sm font-weight-semibold">{customerDetails?.accountManager}</div>
        </div>
      </div>
    </div>
  ) : (
    <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-100 border-top border-bottom py-3">
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Assistant Name
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            // rules={{ required: true }}
            defaultValue={customerDetails?.assistantName}
            name="assistantName"
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Enter Assistant Name"
                name={name}
                value={value}
                onChange={onChange}
                id="assistantName"
              />
            )}
          />
          {errors.assistantName && <ErrorMessage>Assistant Name is required</ErrorMessage>}
        </div>
      </div>
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Assistant Number
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            // rules={{ required: true }}
            rules={{
              required: false,
              validate: (value) =>
                !ValidatePhoneNumberRegEx(value || "") && !!value ? "Invalid phone" : undefined,
            }}
            defaultValue={customerDetails?.assistantNumber}
            name="assistantNumber"
            render={({ onChange, value, name }) => (
              <>
                <PhoneInput
                  containerClass="w-100"
                  country={"in"}
                  value={`${value}`}
                  onChange={(phone, country) => onChange(`${phone}`)}
                />
              </>
            )}
          />
          {errors.assistantNumber?.message && (
            <ErrorMessage>{errors.assistantNumber?.message}</ErrorMessage>
          )}
        </div>
      </div>
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Assistant Designation
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            // rules={{ required: true }}
            defaultValue={customerDetails?.assistantDesignation}
            name="assistantDesignation"
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Enter Assistant Designation"
                name={name}
                value={value}
                onChange={onChange}
                id="assistantDesignation"
              />
            )}
          />
          {errors.assistantDesignation && (
            <ErrorMessage>Assistant Designation is required</ErrorMessage>
          )}
        </div>
      </div>
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Seniority
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            // rules={{
            //   required: false,
            //   validate: (value) => {
            //     return !!value?.trim();
            //   },
            // }}
            name="seniority"
            defaultValue={(customerDetails?.seniority as any)?._id}
            render={({ onChange, value, name }) => (
              <DropdownList
                value={value}
                // defaultValue={value}
                data={seniorityDetails}
                textField={"title"}
                valueField={"_id"}
                placeholder={"Select Seniority"}
                onChange={(val) => {
                  onChange(val._id);
                }}
              />
            )}
          />
          {errors.seniority && <ErrorMessage>Seniority is required</ErrorMessage>}
        </div>
      </div>
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Department
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            // rules={{ required: true }}
            defaultValue={(customerDetails?.department as any)?._id}
            name="department"
            render={({ onChange, value, name }) => (
              <DropdownList
                value={value}
                // defaultValue={value}
                data={departmentDetails}
                textField={"title"}
                valueField={"_id"}
                placeholder={"Enter Department"}
                onChange={(val) => {
                  onChange(val._id);
                }}
              />
            )}
          />
          {errors.department && <ErrorMessage className="row">Department is required</ErrorMessage>}
        </div>
      </div>
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Association Start Date
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            name="associationStartDate"
            defaultValue={customerDetails?.associationStartDate}
            render={({ onChange, value, name }) => (
              <DateInput
                default={value}
                placeholder={"Association Start Date"}
                onChange={onChange}
                {...(watch("associationEndDate") !== undefined
                  ? { maxDate: new Date(watch("associationEndDate") || "") }
                  : {})}
              />
            )}
          />
          {errors.associationStartDate && (
            <ErrorMessage>Association Start Date is required</ErrorMessage>
          )}
        </div>
      </div>
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Association End Date
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            name="associationEndDate"
            defaultValue={customerDetails?.associationEndDate}
            render={({ onChange, value, name }) => (
              <DateInput
                default={value}
                placeholder={"Association End Date"}
                onChange={onChange}
                {...(watch("associationStartDate") !== undefined
                  ? { minDate: new Date(watch("associationStartDate") || "") }
                  : {})}
              />
            )}
          />
          {errors.associationEndDate && (
            <ErrorMessage>Association End Date is required</ErrorMessage>
          )}
        </div>
      </div>
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Director With Company
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            rules={{ required: false }}
            name="isDirectorOfCompany"
            defaultValue={customerDetails?.isDirectorOfCompany}
            render={({ onChange, value, name }) => (
              <DropdownList
                value={value}
                defaultValue={value}
                data={isDirectorOfCompany}
                textField={"name"}
                valueField={"value"}
                placeholder={"Select Director With Company"}
                onChange={(val) => {
                  onChange(val.value);
                }}
              />
            )}
          />
          {errors.isDirectorOfCompany && (
            <ErrorMessage>Director With Company is required</ErrorMessage>
          )}
        </div>
      </div>
      <div className={`d-flex flex-wrap px-4 py-1 contact-form-row active align-items-baseline`}>
        <label htmlFor="" className="col pl-0 pr-2 contact-label font-xs text-gray-500">
          Account Manager
        </label>
        <div className="col pl-2 pr-0 contact-content d-flex flex-wrap align-items-center">
          <Controller
            control={control}
            // rules={{ required: true }}
            defaultValue={customerDetails?.accountManager}
            name="accountManager"
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Enter Account Manager"
                name={name}
                value={value}
                onChange={onChange}
                id="accountManager"
              />
            )}
          />
          {errors.accountManager && <ErrorMessage>Account Manager is required</ErrorMessage>}
        </div>
      </div>
      <div className="col-12 px-4 mt-2  text-right ">
        <div className="px-2">
          <button
            className="btn btn-sm btn-outline-dark mr-1 font-normal"
            onClick={() => onClose()}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-sm btn-primary font-normal">
            Save
          </button>
        </div>
      </div>
    </form>
  );
};

export default EditAssistantDetails;
